import { BaseLayout } from '../../../layouts/BaseLayout';
import ProfilePicture from '../components/ProfilePicture';
import { getServerErrorMsg } from '../../../helpers/errorHandler';
import { useState, useEffect } from 'react';
import { Input, Form, Tooltip, Switch, DatePicker, Select } from 'antd';
import { useGetUserProfileDetails, updateUserProfile } from '../../../api/hooks/useUserQuery';
import { KeyLabelValue } from '../../../types/ProfileTypes';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '../../../contexts/UserContext';
import { Spin } from 'antd';
import { FormFieldTypeEnum } from '../../../enums/FormFieldTypeEnum';
import dayjs from 'dayjs';
import { convertResponseToSections } from '../helpers/ProfileHelper';
import RESOURCE_CONFIGS from '../../../constants/resourceConfig';
import { scrollToTop } from '../../../helpers/utilityHelper';
import { ENVIRONMENT_CONFIG } from '../../../constants/environmentConfig';
import PhoneInput from 'react-phone-number-input';
import { validatePhoneNumberAsync } from '../../../helpers/formHelper';

type ProfileSections = {
  [key: string]: KeyLabelValue[];
};

const Profile = () => {
  const getProfileDetails = useGetUserProfileDetails();
  const updateUserProfileDetails = updateUserProfile();
  const [profileSections, setProfileSections] = useState<ProfileSections>({});
  const [profileImage, setProfileImage] = useState<string | undefined>();
  const { successNotification, errorNotification } = useNotificationContext();
  const [isEditing, setIsEditing] = useState(false);
  const [originalProfileData, setOriginalProfileData] = useState<ProfileSections>({});
  const { t } = useTranslation(['profile']);
  const { setProfileImageToContext, setUserInfoField } = useUserContext();
  const [form] = Form.useForm();
  const [formData, setFormData] = useState<{ [key: string]: Record<string, string> }>({});

  const areAllRequiredFieldsFilled = (): boolean => {
    if (!profileSections) return false;
    const activeSectionKeys: string[] = [];
    if (profileSections.sectionAdminUp) activeSectionKeys.push('sectionAdminUp');
    if (profileSections.sectionAdminDown) activeSectionKeys.push('sectionAdminDown');
    if (profileSections.sectionProfilePictureBelow) {
      activeSectionKeys.push('sectionProfilePictureBelow');
    }
    return activeSectionKeys.every((sectionKey) => {
      const fields = profileSections[sectionKey] || [];
      return fields.every((field) => {
        if (!field.required) return true;
        const value = formData[sectionKey]?.[field.key];
        return typeof value === 'string'
          ? value.trim() !== ''
          : value !== undefined && value !== null;
      });
    });
  };

  useEffect(() => {
    mapToForm(profileSections);
  }, [profileSections]);

  useEffect(() => {
    scrollToTop();
  }, []);

  const mapToForm = (data: ProfileSections) => {
    if (Object.keys(data).length > 0) {
      const initialFormData = Object.keys(data).reduce(
        (acc: { [key: string]: Record<string, string> }, sectionKey: string) => {
          acc[sectionKey] = data[sectionKey].reduce(
            (sectionAcc: Record<string, string>, section: KeyLabelValue) => {
              sectionAcc[section.key] = section.value;
              return sectionAcc;
            },
            {}
          );
          return acc;
        },
        {}
      );
      setFormData(initialFormData);
      form.setFieldsValue(initialFormData);
    }
  };

  const handleEditClick = () => {
    setOriginalProfileData(profileSections);
    setIsEditing(!isEditing);
    form.resetFields();
    fetchProfileDetails();
  };

  useEffect(() => {
    fetchProfileDetails();
  }, [isEditing]);

  const fetchProfileDetails = () => {
    getProfileDetails.mutate(
      {},
      {
        onSuccess: (response: any) => {
          const convertedProfileSections = convertResponseToSections(response.data.data, t);
          setProfileSections(convertedProfileSections);
          const profileData = response.data.data;
          if (profileData.avatar) {
            setProfileImage(profileData.avatar);
            setProfileImageToContext(profileData.avatar);
          }

          const allFieldsArray = [...convertedProfileSections.sectionAdminUp];

          const formValues = allFieldsArray.reduce(
            (acc, field) => {
              acc[field.key] = field.value;
              return acc;
            },
            {} as Record<string, any>
          );

          form.setFieldsValue(formValues);

          setUserInfoField(
            'name',
            profileData.firstName?.trim() || profileData.fullName?.trim().split(' ')[0] || ''
          );
        },
        onError: (response) => {
          const errorMsg = getServerErrorMsg(response);
        },
      }
    );
  };

  const renderSections = (sections: KeyLabelValue[], sectionKey: string, showLabels: boolean) => {
    return sections.map((section) => {
      const value = formData[sectionKey]?.[section.key];
      const disabled = !section.editable;
      const handleValueChange = (val: any) => {
        setFormData((prev) => ({
          ...prev,
          [sectionKey]: {
            ...prev[sectionKey],
            [section.key]: val,
          },
        }));
      };
      let inputComponent = null;
      const translatedLabel = t(`profile:${section.key}`, { defaultValue: section.label });

      switch (section.type) {
        case FormFieldTypeEnum.SWITCH:
          inputComponent = (
            <Switch
              checked={value === 'true'}
              onChange={(checked) => handleValueChange(checked ? 'true' : 'false')}
              disabled={disabled}
            />
          );
          break;

        case FormFieldTypeEnum.EMAIL:
        case FormFieldTypeEnum.INPUT:
          inputComponent = (
            <Input
              type={section.type === FormFieldTypeEnum.EMAIL ? 'email' : 'text'}
              value={value || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              disabled={disabled}
            />
          );
          break;

        case FormFieldTypeEnum.TELEPHONE:
          inputComponent = (
            <PhoneInput
              international
              defaultCountry={ENVIRONMENT_CONFIG.COUNTRY_CODE}
              countryCallingCodeEditable={false}
              value={value || ''}
              onChange={(val) => {
                form.setFieldValue(section.key, val);
                handleValueChange(val);
              }}
              disabled={disabled}
              className="w-full"
            />
          );
          break;

        case FormFieldTypeEnum.DATE:
          inputComponent = (
            <DatePicker
              value={value ? dayjs(value) : null}
              onChange={(date, dateString) => handleValueChange(dateString)}
              disabled={disabled}
              className="w-full"
              format={RESOURCE_CONFIGS.DATE_TIME.DEFAULT_FORMAT}
            />
          );
          break;

        case FormFieldTypeEnum.DATE_TIME:
          inputComponent = (
            <DatePicker
              showTime
              value={value ? dayjs(value) : null}
              onChange={(date, dateString) => handleValueChange(dateString)}
              disabled={disabled}
              className="w-full"
              format={RESOURCE_CONFIGS.DATE_TIME.DATE_WITH_TIME}
            />
          );
          break;

        case FormFieldTypeEnum.SELECT:
          inputComponent = (
            <Select
              value={value || undefined}
              onChange={(val) => handleValueChange(val)}
              disabled={disabled}
              style={{ width: '100%' }}
              options={(section.options || []).map((opt) => ({
                ...opt,
                label: t(`profile:${opt.label}`, { defaultValue: opt.label }),
              }))}
            />
          );
          break;
      }

      return (
        <div key={section.key} className="w-full">
          <label className="text-left font-medium text-label-black">
            {translatedLabel}
            {section.required && (
              <span className="ml-1" style={{ color: 'red' }}>
                *
              </span>
            )}
          </label>
          <Form
            form={form}
            initialValues={formData[sectionKey]}
            onValuesChange={(_changedValues, allValues) => {
              setFormData((prev) => ({
                ...prev,
                [sectionKey]: allValues,
              }));
            }}
            className="w-full md:flex-1"
          >
            <Form.Item name={section.key} rules={section.rules} className="mb-4 mt-4">
              <Tooltip
                title={
                  disabled && value
                    ? section.type === FormFieldTypeEnum.DATE
                      ? dayjs(value).format(RESOURCE_CONFIGS.DATE_TIME.DEFAULT_FORMAT)
                      : t(`profile:${value}`, { defaultValue: value })
                    : ''
                }
                placement="top"
              >
                {inputComponent}
              </Tooltip>
            </Form.Item>
          </Form>
        </div>
      );
    });
  };

  const renderSectionsNonEdit = (sections: KeyLabelValue[], showLabels: boolean) => {
    if (!sections) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center">
          <Spin />
        </div>
      );
    }

    return sections.map((section) => {
      const label = t(`profile:${section.key}`, { defaultValue: section.key });
      let valueContent = ' - ';

      if (section.value?.trim()) {
        if (section.type === FormFieldTypeEnum.DATE) {
          valueContent = dayjs(section.value).format(RESOURCE_CONFIGS.DATE_TIME.DEFAULT_FORMAT);
        } else if (section.type === FormFieldTypeEnum.DATE_TIME) {
          valueContent = dayjs(section.value).format(RESOURCE_CONFIGS.DATE_TIME.DATE_WITH_TIME);
        } else {
          valueContent = t(`profile:${section.value}`, {
            defaultValue: section.value,
          });
        }
      }

      return (
        <div
          key={section.key}
          className="flex flex-col pb-4 md:flex-col md:items-start md:space-y-0"
        >
          {showLabels && (
            <label className="mb-4 w-full text-left font-medium text-label-black">{label}</label>
          )}

          {section.type === FormFieldTypeEnum.SWITCH ? (
            <Switch checked={section.value === 'true'} disabled className="w-[50px]" />
          ) : (
            <Input
              value={valueContent}
              disabled
              className={`mt-4 w-full ${
                section.key === 'name' ? 'font-medium text-label-black' : ''
              }`}
            />
          )}
        </div>
      );
    });
  };

  const handleSave = async () => {
    const getEmptyStringIfDash = (value: string) => (value === '-' ? '' : value);

    const updatedProfileData: Record<string, any> = {};

    Object.keys(profileSections).forEach((sectionKey) => {
      const originalFields = originalProfileData?.[sectionKey] || [];
      const originalMap = new Map<string, any>();
      originalFields.forEach((field) => {
        originalMap.set(field.key, field.value ?? '');
      });

      profileSections[sectionKey].forEach((field) => {
        if (field.editable) {
          const newValue = formData?.[sectionKey]?.[field.key];
          const originalValue = originalMap.get(field.key);

          const normalize = (val: string | null | undefined) => {
            const str = String(val ?? '').trim();
            return str === '-' ? '' : str;
          };

          if (normalize(newValue) !== normalize(originalValue)) {
            updatedProfileData[field.key] =
              typeof newValue === 'string' ? getEmptyStringIfDash(newValue.trim()) : newValue;
          }
        }
      });
    });

    const isUserDataChanged = hasChanges();
    if (isUserDataChanged) {
      if (updatedProfileData?.contactNumber) {
        const isValid = await validatePhoneNumberAsync(t, updatedProfileData.contactNumber);
        if (!isValid) {
          errorNotification(t('invalidPhoneNumber'));
          return;
        }
      }
      updateUserProfileDetails.mutate(updatedProfileData, {
        onSuccess: (response: any) => {
          successNotification(t('profileUpdatedSuccessfully'));
          const convertedProfileSections = convertResponseToSections(
            response.data.data.userProfileData,
            t
          );
          setProfileSections(convertedProfileSections);
        },
        onError: (response: any) => {
          const errorMsg = getServerErrorMsg(response);
          errorNotification(errorMsg);
        },
      });
      setIsEditing(false);
    } else {
      errorNotification(t('noChangesMade'));
    }
  };

  const handleCancel = () => {
    setProfileSections(originalProfileData);
    setIsEditing(!isEditing);
  };

  const hasChanges = () => {
    if (!originalProfileData) return false;

    const getValue = (array: Array<{ key: string; value: any }>, key: string) => {
      return array.find((item) => item.key === key)?.value ?? '';
    };

    const getNormalizedValue = (value: string | undefined | null): string => {
      const str = String(value ?? '').trim();
      return str === '-' ? '' : str;
    };

    const sectionsToCheck = ['sectionAdminUp', 'sectionAdminDown', 'sectionProfilePictureBelow'];

    return sectionsToCheck.some((section) => {
      const originalFields = originalProfileData[section] || [];

      const currentObject = formData[section] || {};
      const currentFields = Object.entries(currentObject).map(([key, value]) => ({
        key,
        value,
        editable: originalFields.find((f) => f.key === key)?.editable ?? false,
      }));

      return currentFields.some((field) => {
        if (!field.editable) return false;
        const originalValue = getValue(originalFields, field.key);
        return getNormalizedValue(field.value) !== getNormalizedValue(originalValue);
      });
    });
  };

  return (
    <div className="h-screen-minus-100 flex w-full flex-col items-center">
      <div className="flex w-full flex-col justify-center md:flex-col">
        <div
          className={`flex h-full w-full flex-col items-center justify-center rounded-br-none rounded-tr-none bg-gradient-to-r from-white to-secondary p-4 md:flex-row md:rounded-br-2xl md:rounded-tr-2xl`}
        >
          <ProfilePicture initialImage={profileImage} />
          <div className="items-left secondDiv flex h-full w-full flex-col justify-center p-4">
            <h1 className="text-center text-2xl font-medium md:text-left md:text-3xl">
              {profileSections.sectionProfilePictureBelow?.find((field) => field.key === 'name')
                ?.value || ''}
            </h1>
            <h3 className="mt-4 text-center text-base font-normal leading-[100%] tracking-[0px] md:text-left md:text-xl">
              {profileSections.sectionProfilePictureBelow
                ?.find((field) => field.key === 'role')
                ?.value?.toUpperCase() || ''}
            </h3>
          </div>
        </div>
        <div className="mt-8 flex h-auto w-full flex-col items-center justify-center px-0 md:mt-0 md:w-full">
          <div className="mb-4 h-auto w-full border-primary-gray-300 p-0 pb-4 md:pt-8">
            <div className="flex flex-col md:flex-row">
              <div className="w-full">
                <h1 className="mb-12 text-2xl font-bold text-black text-primary">
                  {t('profile:personalInfo')}
                </h1>
                <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
                  {profileSections.sectionAdminUp &&
                    (isEditing
                      ? renderSections(profileSections.sectionAdminUp, 'sectionAdminUp', true)
                      : renderSectionsNonEdit(profileSections.sectionAdminUp, true))}
                </div>
              </div>
            </div>
            <div className="flex w-full flex-row items-center justify-start">
              {isEditing && (
                <button
                  onClick={handleCancel}
                  className="mr-4 mt-8 h-10 rounded border border-primary bg-transparent px-6 py-2 text-primary"
                >
                  {t('cancel')}
                </button>
              )}
              <button
                onClick={isEditing ? handleSave : handleEditClick}
                className={`mt-8 h-10 rounded py-2 ${isEditing ? 'btn-primary text-white' : 'btn-primary text-white'}`}
                disabled={isEditing && !areAllRequiredFieldsFilled()}
              >
                {isEditing ? t('save') : t('edit')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProfilePage = () => {
  const { t } = useTranslation('profile');
  return <BaseLayout content={<Profile />} pageTitle={t('profile')}></BaseLayout>;
};

export default ProfilePage;
